import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Icon, NewTable, useToast } from '@vallourec-ui/vallourec-ui'
import { isAxiosError } from 'axios'

import { useTranslation } from 'react-i18next'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Container } from './style'

import { TableWrapper } from '../Flow/_component/DataToResult/style'
import EditModal from './_components/EditModal'
import { PageProps, Remark, RemarkReport } from '@/@types/global'
import { configRemark } from '@/api/post'
import { useApp } from '@/stores/AppStore'

type ButtonCellProps = {
  row: { original: any }
  onEdit: (item: any) => void
}

const ButtonCell: React.FC<ButtonCellProps> = ({ row, onEdit }) => (
  <Button
    onClick={() => {
      onEdit(row.original)
    }}
    key="confirm-button"
    variant="secondary-grey"
  >
    <Icon name="EditLine" />
  </Button>
)

type RemarksCellProps = {
  remarks: Remark[]
  t: (ns: string, key: string) => string
}

const RemarksCell: React.FC<RemarksCellProps> = ({ remarks, t }) => (
  <div>
    {Array.isArray(remarks)
      ? remarks.map((remark) => (
          <div
            key={`${remark.line}-${remark.remark}`}
            style={{ lineHeight: '1.8' }}
          >
            <span
              style={{
                marginRight: 8,
                fontWeight: 'bold',
                alignSelf: 'flex-start',
              }}
            >
              •
            </span>
            {t('remark.line', 'Line')} {remark.line} : {remark.remark}
          </div>
        ))
      : null}
  </div>
)

const renderRemarksCell =
  (t: (ns: string, key: string) => string) =>
  ({ row }: { row: { original: TableRow } }) => (
    <RemarksCell remarks={row.original.remarks} t={t} />
  )

const RemarkDefinition: React.FC<PageProps> = ({ title, subtitle }) => {
  usePageTitle({ title, subtitle })
  const { setIsLoading } = useApp()
  const { toast } = useToast()

  const { t } = useTranslation()

  const [remarks, setRemarks] = useState<RemarkReport[]>([])

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<any>(undefined)

  type TableRow = {
    id: string
    remarks: Remark[]
    displayRemarks: string
    name: string
    type: number
  }

  const columns = useMemo(
    () => [
      {
        id: 'button',
        accessorKey: 'button',
        header: '',
        cell: ({ row }: { row: { original: TableRow } }) => (
          <ButtonCell
            row={row}
            onEdit={(item) => {
              setSelectedItem(item)
              setModalIsOpen(true)
            }}
          />
        ),
      },
      {
        id: 'name',
        accessorKey: 'displayName',
        header: t<string>('remark.type', 'Type'),
      },
      {
        id: 'remarks',
        accessorKey: 'remarks',
        header: t<string>('remark.remarks', 'Remarks'),
        cell: renderRemarksCell(t),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, remarks]
  )

  const removeDuplicateRemarks = (items: RemarkReport[]) => {
    items.forEach((item) => {
      const uniqueRemarks: Remark[] = []
      const seenRemarks = new Set()

      item.remarks.forEach((elemt) => {
        const remarkKey = `${elemt.line}-${elemt.remark}`
        if (!seenRemarks.has(remarkKey)) {
          seenRemarks.add(remarkKey)
          uniqueRemarks.push(elemt)
        }
      })

      item.remarks = uniqueRemarks
    })

    return items
  }

  const fetchData = async () => {
    try {
      setIsLoading(true)
      setRemarks([])
      const response = (await configRemark({})) as unknown as {
        items: RemarkReport[]
      }
      const items =
        response && response.items
          ? removeDuplicateRemarks(response.items)
          : null

      if (items) {
        items.forEach((item) => {
          item.displayName = t(
            `remark.types.${String(item.name)?.toLowerCase()}`
          )
          item.displayRemarks = item.remarks
            .map(
              (remark) =>
                `${t<string>('', 'Line')} ${remark.line} : ${remark.remark}`
            )
            .join(', ')
        })
      }
      setRemarks(items ?? [])
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
      if (isAxiosError(err)) {
        toast({
          title: t('error'),
          description: err?.message
            ? err?.message
            : t<string>('errorLoadingData'),
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t])

  const handleOnSave = async (data: RemarkReport) => {
    setModalIsOpen(false)
    setIsLoading(true)
    try {
      await configRemark({ save: data })
      toast({
        title: t('success'),
        description: t('successDescription'),
        type: 'success',
      })
      await fetchData()
    } catch (err) {
      if (isAxiosError(err)) {
        toast({
          title: t('error'),
          description: err?.message
            ? err?.message
            : t<string>('errorDescription'),
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const renderTable = useCallback(() => {
    return remarks?.length > 0 ? (
      <TableWrapper>
        <NewTable.Root
          key="table-with-data"
          columns={columns}
          data={remarks}
          hasPagination
          isBordered
          hasSorting
        />
      </TableWrapper>
    ) : (
      <NewTable.Root key="table-without-data" data={[]} columns={columns} />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remarks, t])

  return (
    <Container>
      {renderTable()}
      <EditModal
        isOpen={modalIsOpen}
        data={selectedItem ?? ({} as any)}
        title={selectedItem?.displayName}
        setIsOpen={setModalIsOpen}
        onOutsideClick={() => {
          setModalIsOpen(false)
          setSelectedItem(undefined)
        }}
        onConfirm={handleOnSave}
      />
    </Container>
  )
}

export default RemarkDefinition
